/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

angular.module('prism-ui.components').service('AsperaConnectService', function (
    this: JS5Object,
    $q: JS5Object,
    _: JS5Object,
    AW4: any,
    EndPointService: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    DynamicPropertiesService: JS5Object
) {
    let AsperaWeb: JS5Object;
    let filesReadyForUpload: JS5Object[] = [];
    const asperaPropertyPrefix = 'asperaConnect.';
    const asperaPropertyNames = [
        'initializationTimeout', 'minVersion', 'destinationRoot',
        'remoteHost', 'remoteUser', 'remotePassword',
        'targetRateKbps', 'virtualRoot'
    ];

    this.unregisterAllFiles = () => {
        filesReadyForUpload = [];
    };

    this.isFileRegistered = (asperaFileObject: JS5Object) => {
        let fileName = _.get(asperaFileObject, 'name');
        return _.some(filesReadyForUpload, {name: fileName});
    };

    this.registerFileForUpload = (asperaFileObject: JS5Object) => {
        console.info(`AsperaConnectService.registerFileForUpload(): registering ${asperaFileObject.name}`);

        let registered = false;
        if (this.isFileRegistered(asperaFileObject)) {
            let message = 'File already selected for upload.';
            let detail =
                `The local file "${asperaFileObject.name}" has` +
                ` already been selected for upload. Please` +
                ` select a different file, or add this file` +
                ` after the current selections have been saved.`;
            NotificationService.createFailureNotification(message, detail, true, 0);
        } else {
            filesReadyForUpload.push(asperaFileObject);
            registered = true;
        }
        return registered;
    };

    this.unregisterFile = (asperaFileObject: JS5Object) => {
        if (asperaFileObject) {
            console.info(`AsperaConnectService.unregisterFile(): unregistering` +
                         ` ${_.get(asperaFileObject, 'name')}`);
            _.remove(filesReadyForUpload, (file: JS5Object) => {
                return file.name === _.get(asperaFileObject, 'name');
            });
        }
    };

    let initializationFailed = false;

    this.reset = () => {
        this.asperaConnectProperties = null;
        delete this.asperaConnectProperties;
        this.unregisterAllFiles();
        AsperaWeb = null;

        initializationFailed = false;
        this.initialized = false;
        this.initializationDeferred = $q.defer();
    };

    this.initialize = (targetSubdirectory: string) => {
        return DynamicPropertiesService.queryForProperties().then(() => {
            this.reset();

            const asperaConnectEnabled =
                DynamicPropertiesService.getPropValueAsBoolean('mediaTabRemoteUpload.enabled', false) &&
                DynamicPropertiesService.getPropValueAsBoolean('asperaConnect.enabled', false);

            if (!asperaConnectEnabled) {
                return;
            }

            this.asperaConnectProperties = {};
            _.forEach(asperaPropertyNames, (propertyName: string) => {
                let value = DynamicPropertiesService.getPropValue(asperaPropertyPrefix + propertyName, null);
                if (value !== null) {
                    this.asperaConnectProperties[propertyName] = value;
                }
            });

            this.destinationRoot = targetSubdirectory;

            let connectInstaller = new AW4.ConnectInstaller({
                sdkLocation: EndPointService.asperaConnectSdkPath,
            });

            const defaultInitializationTimeout = 15000;

            let initializationTimeout = _.get(this,
                'asperaConnectProperties.initializationTimeout',
                defaultInitializationTimeout);

            AsperaWeb = new AW4.Connect({
                connectLaunchWaitTimeoutMs: initializationTimeout,
                sdkLocation: EndPointService.asperaConnectSdkPath,
                minVersion: this.asperaConnectProperties.minVersion,
                dragDropEnabled: true
            });

            this.initializedDeferred = $q.defer();

            const statusEventListener = (eventType: string, data: JS5Object) => {
                if (eventType === AW4.Connect.EVENT.STATUS) {
                    switch (data) {
                        case AW4.Connect.STATUS.INITIALIZING:
                            this.initialized = false;
                            console.info(`AsperaConnectService.initialize(): Aspera indicates that it's initializing`);
                            connectInstaller.showLaunching();
                            break;

                        case AW4.Connect.STATUS.FAILED:
                            this.initialized = false;
                            initializationFailed = true;
                            console.warn(`AsperaConnectService.initialize(): Aspera indicates that it's failed!`);
                            connectInstaller.showDownload();
                            this.initializedDeferred.reject(this.asperaConnectProperties.minVersion);
                            break;

                        case AW4.Connect.STATUS.OUTDATED:
                            this.initialized = false;
                            initializationFailed = true;
                            console.warn(`AsperaConnectService.initialize(): Aspera indicates that it's outdated`);
                            connectInstaller.showUpdate();
                            this.initializedDeferred.reject(this.asperaConnectProperties.minVersion);
                            break;

                        case AW4.Connect.STATUS.RUNNING:
                            if (!initializationFailed) {
                                this.initialized = true;
                                console.info(`AsperaConnectService.initialize(): Aspera indicates that it's running`);
                                connectInstaller.connected();
                                this.initializedDeferred.resolve();
                            }
                            break;

                        case AW4.Connect.STATUS.RETRYING:
                            this.initialized = false;
                            console.info(`AsperaConnectService.initialize(): Aspera indicates that it's retrying`);
                            break;

                        default:
                            console.info(`AsperaConnectService.initialize(): Unhandled event...`);
                            break;
                    }
                }
            };

            AsperaWeb.addEventListener(AW4.Connect.EVENT.STATUS, statusEventListener);
            AsperaWeb.addEventListener(AW4.Connect.EVENT.TRANSFER, handleTransferEvents);

            AsperaWeb.initSession();

            return this.initializedDeferred.promise;
        });
    };

    this.setDropTarget = (dropTarget: string, dropTargetListener: JS5Function) => {
        let options: JS5Object = {
            dragEnter: false,
            dragLeave: false,
            dragOver: false,
            drop: true
        };

        AsperaWeb.setDragDropTargets(`.${dropTarget}`, options, dropTargetListener);
    };

    const startTransfer = (transferSpec: JS5Object, connectSettings: JS5Object, token: string) => {
        if (typeof token !== 'undefined' && token !== '') {
            transferSpec.authentication = 'token';
            transferSpec.token = token;
        }

        const onError = (obj: JS5Object) => {
            let failureMessage = JSON.stringify(obj, null, 4);
            console.error(`Failed to start :  ${failureMessage}`);
            NotificationService.createFailureNotification('Failed to start!', failureMessage, true);
        };

        const onSuccess = () => {
            let fileNames: string[] = [];

            _.forEach(transferSpec.paths, (path: JS5Object) => {
                fileNames.push(path.source.slice(path.source.lastIndexOf('/') + 1));
            });
            NotificationService.createSuccessNotification(`Aspera transfer initiated: ${_.join(fileNames, ', ')}`,
                5000);

            this.unregisterAllFiles();
        };

        AsperaWeb.startTransfer(
            transferSpec,
            connectSettings,
            {
                error: onError,
                success: onSuccess
            });
    };

    this.beginUpload = (targetPath: string) => {
        let transferSpec: JS5Object = {
            cookie: `u-${new Date().getTime()}`,
            create_dir: true,
            destination_root: `${this.asperaConnectProperties.destinationRoot}${targetPath}`,
            direction: 'send',
            paths: [],
            remote_host: this.asperaConnectProperties.remoteHost,
            remote_user: this.asperaConnectProperties.remoteUser,
            remote_password: this.asperaConnectProperties.remotePassword,
            resume: 'sparse_checksum',
            target_rate_kbps: this.asperaConnectProperties.targetRateKbps
        };

        _.forEach(filesReadyForUpload, (file: any) => {
            transferSpec.paths.push({
                source: file.name
            });
            console.info(`AsperaConnectService.beginUpload(): added ${file.name} to upload batch`);
        });

        if (transferSpec.paths.length > 0) {
            let connectSettings = {
                allow_dialogs: 'true'
            };

            startTransfer(transferSpec, connectSettings, '');
        } else {
            console.warn(`AsperaConnectService.beginUpload(): initiated transfer of 0 files`);
        }
    };

    const showAsperaTransferErrors = (asperaErrors: string[]) => {
        asperaErrors = _.uniq(asperaErrors);
        if (_.size(asperaErrors)) {
            _.forEach(asperaErrors, (asperaError: string) => {
                let message = `Aspera Connect failed to start the transfer.`;
                let detail = `The Aspera Connect plugin provided the following error message: "${asperaError}"`;
                NotificationService.createFailureNotification(message, detail, true, 0);
            });
        }
    };

    const handleAsperaTransferEvent = (transfersJsonObject: JS5Object) => {
        let asperaErrors: string[] = [];

        _.forEach(transfersJsonObject.transfers, (transfer: JS5Object) => {
            console.info(`AsperaConnectService.handleTransferEvents(): ${transfer.title} - ${transfer.status}`);
            switch (transfer.status) {
                case 'failed':
                    let asperaError = `${transfer.title} - ${transfer.error_desc}`;
                    asperaErrors.push(asperaError);
                    break;

                case 'completed':
                default:
                    break;
            }
        });

        showAsperaTransferErrors(asperaErrors);
    };

    const handleTransferEvents = (event: string, transfersJsonObject: JS5Object) => {
        switch (event) {
            case 'transfer':
                handleAsperaTransferEvent(transfersJsonObject);
                break;
        }
    };

    let getTransferEvents = (iterationToken: any = undefined) => {
        let callbacks = {
            success: (allTransfers: JS5Object) => {
                if (typeof allTransfers.error === 'undefined') {
                    let resultcount = allTransfers.result_count;
                    let transfers = allTransfers.transfers;

                    // Put any logic related to transfers in this block
                }
            }
        };

        AsperaWeb.getAllTransfers(iterationToken, callbacks);
    };

    const $onInit = () => {
        //
        // Put service entry stuff here:
        //

        this.reset();
    };

    $onInit();
});
